// Copyright (c) 2019-2025 Cigent Technology Inc. All Rights Reserved.
import { Component, OnInit } from '@angular/core';
import { HeaderService } from 'src/app/header.service';
import { Router } from '@angular/router';
import { UserService } from 'src/services/user-service';
import { MatDialog } from '@angular/material/dialog';
import { NotificationService } from 'src/services/notification-service';
import { UserGroupRoleComponent } from 'src/app/dialogs/user-group-role/user-group-role.component';
import { D3eService } from 'src/services/d3e-service';
import {UsersStats, IUser, IManageUser} from 'src/types/user';
import { EditUserDialogComponent } from 'src/app/dialogs/edit-user/edit-user.component';
import {IIntegration, PartnerTypes} from '../../../types/integrations';
import {EditExpDateComponent} from '../../dialogs/edit-exp-date/edit-exp-date.component';
import {map, switchMap} from 'rxjs/operators';
import {EMPTY, Observable} from 'rxjs';
import {IExpiryResult} from './expiring_subs';
import {TranslateService} from '@ngx-translate/core';
import {ReparentDialogComponent} from '../../dialogs/reparent-dialog/reparent-dialog.component';
import { CreateAdminEventComponent } from '../../dialogs/create-admin-event/create-admin-event.component';
import { ReParentUserDialogComponent } from '../../dialogs/move-user-dialog/re-parent-user-dialog.component';
import {DeleteOrganizationDialogComponent} from '../../dialogs/delete-organization-dialog/delete-organization-dialog.component';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {selectUser} from '../../store/user/user.selectors';
import { AppState } from 'src/app/store/app.state';
import { Store } from '@ngrx/store';

@UntilDestroy({ checkProperties: true })
@Component({
    selector: 'app-admin',
    templateUrl: './admin.component.html',
    styleUrls: ['./admin.component.scss'],
    standalone: false
})
export class AdminComponent implements OnInit {
  user$: Observable<IManageUser> = this.store.select(selectUser());

  loggedInUser: IUser;
  initialized$: Observable<boolean>;

  subs: IExpiryResult[] = [];
  subsTableColumns = ['groupName', 'contactEmail', 'subscription_id', 'exp_timestamp', 'latest_endpoint_dttm', 'exp_edit_icon'];

  superusers: IUser[] = [];
  superusersTableColumns = ['username'];

  administrators: IUser[] = [];
  administratorsTableColumns = ['username', 'email', 'company', 'admin_type'];

  users: IUser[] = [];
  usersTableColumns = ['username', 'administrator', 'active', 'business_edition', 'create_child',
                       'manage_child'];

  usersStats: UsersStats;

  activeIntegrations: IIntegration[] = [];
  activeIntegrationsTableColumns = ['adminEmail', 'partnerId'];

  licensesUsed$ = this.d3eService.getLicensesUsed();

  fAddUser: string;
  fManageUser: string;
  fRecent: string;
  fTotalUsers: string;
  fUsers: string;
  fNone: string;
  fUsername: string;
  fCompany: string;
  fAdmin: string;
  fActive: string;
  fAdvA: string;
  fAdvB: string;
  fD3EA: string;
  fD3EB: string;
  fCreate: string;
  fChild: string;
  fManage: string;
  fPassExpire: string;
  fLocked: string;
  fExpiringSub: string;
  fEmail: string;
  fLastLogin: string;
  fSubID: string;
  fExpireDate: string;
  fActiveIntegrations: string;
  fPartner: string;
  fSuperUser: string;
  fAdministrator: string;
  fType: string;
  fExpirationUpdated: string;
  fCreatedUserSuccess: string;

  title: string;
  constructor(
    private headerService: HeaderService,
    private router: Router,
    private d3eService: D3eService,
    private userService: UserService,
    private notification: NotificationService,
    public dialog: MatDialog,
    private translate: TranslateService,
    private readonly store: Store<AppState>,
  ) {
    this.translate.get(['ADM_ADDU', 'ADM_MANAGEU', 'ADM_RECENT', 'ADM_TOTALU',
      'ADM_USERS', 'ADM_NONE', 'ADM_UNAME', 'ADM_COMPANY', 'ADM_ADMIN', 'ADM_ACTIVE', 'ADM_ADVANCEDA',
      'ADM_ADVANCEDB', 'ADM_D3E', 'ADM_EDITION', 'ADM_CREATE', 'ADM_CHILD', 'ADM_MANAGE', 'ADM_PASSEXPIRE',
      'ADM_LOCKED', 'ADM_EXPIRE', 'ADM_EMAIL', 'ADM_LOGIN', 'ADM_SUB_ID', 'ADM_EXPIRE_DATE', 'ADM_ACTIVE_INT',
      'ADM_PARTNER', 'ADM_SUPER_USER', 'ADM_ADMINISTRATOR', 'ADM_TYPE', 'TITLE_ADMIN', 'ADM_EXPIRATION_UPDATED',
      'ADM_CREATED_USER_SUCCESS', ]).subscribe(vals => {
       this.fAddUser = vals['ADM_ADDU'];
       this.fManageUser = vals['ADM_MANAGEU'];
       this.fRecent = vals['ADM_RECENT'];
       this.fTotalUsers = vals['ADM_TOTALU'];
       this.fUsers = vals['ADM_USERS'];
       this.fNone = vals['ADM_NONE'];
       this.fUsername = vals['ADM_UNAME'];
       this.fCompany = vals['ADM_COMPANY'];
       this.fAdmin = vals['ADM_ADMIN'];
       this.fActive = vals['ADM_ACTIVE'];
       this.fAdvA = vals['ADM_ADVANCEDA'];
       this.fAdvB = vals['ADM_ADVANCEDB'];
       this.fD3EA = vals['ADM_D3E'];
       this.fD3EB = vals['ADM_EDITION'];
       this.fCreate = vals['ADM_CREATE'];
       this.fChild = vals['ADM_CHILD'];
       this.fManage = vals['ADM_MANAGE'];
       this.fPassExpire = vals['ADM_PASSEXPIRE'];
       this.fLocked = vals['ADM_LOCKED'];
       this.fExpiringSub = vals['ADM_EXPIRE'];
       this.fEmail = vals['ADM_EMAIL'];
       this.fLastLogin = vals['ADM_LOGIN'];
       this.fSubID = vals['ADM_SUB_ID'];
       this.fExpireDate = vals['ADM_EXPIRE_DATE'];
       this.fActiveIntegrations = vals['ADM_ACTIVE_INT'];
       this.fPartner = vals['ADM_PARTNER'];
       this.fSuperUser = vals['ADM_SUPER_USER'];
       this.fAdministrator = vals['ADM_ADMINISTRATOR'];
       this.fType = vals['ADM_TYPE'];
       this.fExpirationUpdated = vals['ADM_EXPIRATION_UPDATED'];
       this.title = vals['TITLE_ADMIN'];
       this.fCreatedUserSuccess = vals['ADM_CREATED_USER_SUCCESS'];
    });
    this.headerService.setTitle(this.title);
    this.headerService.setSubtitle('');
    this.headerService.setIcon('mdi-account-supervisor');
    this.headerService.setNavMenuText('');
    this.headerService.showUserFilter(false);
  }

  ngOnInit() {
    this.initialized$ = this.userService.subscribeToUser().pipe(
      untilDestroyed(this),
      switchMap(user => {
        this.loggedInUser = user;
        return this.getExpiringSubs();
      }),
      map(response => {
        this.subs = response.results;
        this.getUsers();
        this.getSuperusers();
        this.getActiveIntegrations();
        this.getUserStats();
        return true;
      })
    );
  }

  getUserStats() {
    this.userService.api_get_users_stats()
        .pipe(untilDestroyed(this))
        .subscribe(stats => {
      this.usersStats = stats;
    });
  }

  getExpiringSubs() {
    // 14 days into the future, 14 days into the past
    return this.d3eService.get_subscriptions_expiring(30, 60);
  }

  getUsers() {
    this.userService.api_get_all_users()
        .pipe(untilDestroyed(this))
        .subscribe(users => {
          this.users = users;
          this.administrators = users.filter(user => (user.administrator === 1 || user.administrator_ro === 1));
    });
  }

  getSuperusers() {
    this.userService.api_get_super_users()
        .pipe(untilDestroyed(this))
        .subscribe(users => {
            this.superusers = users;
    });
  }

  getAdminType(user): string {
    if (user.administrator) {
      return 'Read-Write';
    } else if (user.administrator_ro) {
      return 'Read-Only';
    }
  }

  getActiveIntegrations() {
    this.d3eService.get_active_integrations()
        .pipe(untilDestroyed(this))
        .subscribe(response => {
            this.activeIntegrations = response.integrations;
    });
  }

  manageGroups() {
    this.dialog.open(UserGroupRoleComponent, {
      width: 'auto',
      height: 'auto',
      data: {}
    });
  }

  isAdmin() {
    return this.loggedInUser?.administrator;
  }

  onUserRowClick(user) {
    if (!this.loggedInUser.administrator) {
      return;
    }
    const dialogRef = this.dialog.open(EditUserDialogComponent, {
      width: '500px',
      data: { user: user },
    });
    dialogRef.afterClosed().subscribe(result => {
      this.getUsers();
    });
  }

  getPartnerName(partner_id) {
    switch (partner_id) {
      case PartnerTypes.CARBON_BLACK:
        return 'Carbon Black';
      case PartnerTypes.CISCO_AMP:
        return 'Cisco Secure Endpoint';
      case PartnerTypes.SOPHOS:
        return 'Sophos';
      case PartnerTypes.CYBERARK_EPM:
        return 'Cyberark EPM';
      default:
        return `Unknown Partner ID (${partner_id})`;
    }
  }

  editExpDate(result: IExpiryResult) {
    const dialogRef = this.dialog.open(EditExpDateComponent, {
      width: '350px',
      data: {
        date: new Date(result.subscription.exp_timestamp),
        minDate: new Date(Date.now() - (24 * 60 * 60 * 1000)),
        maxDate: new Date(2030, 0, 1)
      },
    });
    dialogRef.afterClosed().pipe(
        switchMap(newExpDate => {
          if (newExpDate) {
            return this.d3eService.extendSubscription(
                result.subscription.subscription_id,
                newExpDate
            );
          }
          return EMPTY;
        }),
        switchMap(
            switchmap_result => {
              this.notification.showSuccess(this.fExpirationUpdated);
              return this.getExpiringSubs();
            })
        ).subscribe(
            response => {
                this.subs = response.results;
            },
            () => {
                    this.notification.showError(this.translate.instant('ERROR_ENCOUNTERED'));
                }
            );
  }

    reparentGroup() {
        this.dialog.open(ReparentDialogComponent, {
           width: '400px'
        });
    }

    addAdminEvent() {
        this.dialog.open(CreateAdminEventComponent, {
            width: '550px',
            height: 'auto',
            maxHeight: '800px',
            data: {}
        });
    }

    moveUser() {
        const dialogRef = this.dialog.open(ReParentUserDialogComponent, {
          width: '550px',
          height: 'auto',
        });
        dialogRef.afterClosed().subscribe(success => {
          if (success) {
            this.userService.refreshOrgs();
            this.getUsers();
            this.notification.showSuccess(this.translate.instant('USER_MOVED'));
          }
        });
    }

    deleteOrganization() {
        this.dialog.open(DeleteOrganizationDialogComponent, {
            width: '450px',
            height: 'auto',
            maxHeight: '80%',
            data: {}
        });
    }

  raiseException(message: string) {
    throw new Error(message);
  }

  protected readonly Object = Object;
}
